import { reportShell, reportOptions } from '../../reports/utils/shell';
import { formatDateOnly } from '../../../common/utils/date';
import { DX_RESULTS, DX_RESULT_BY_VALUE, PREG_STATE } from '../../../core/constants/dx';
import { storageLabel } from '../../../core/constants/transfers';

// Informe PDF de Dx por OPU: estado de cada preñez + última palpación + las FECHAS
// ESTIMADAS SIGUIENTES (próximo dx y parto) — derivadas del bloque `forecast`/`next_dx`
// del ensamblado de preñez (0026/0030). El front arma el HTML, el backend devuelve el
// PDF (admin-reports.md). Las fechas son estimaciones (nota al pie).
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const date = (d) => (d ? esc(formatDateOnly(d)) : '—');

const recipientLabel = (r) => (r ? `${r.number || ''}${r.name ? ` · ${r.name}` : ''}`.trim() : '—');

// Última palpación con resultado (mayor number con result).
function lastDx(diagnostics) {
    const done = (diagnostics || []).filter((d) => d.result != null);
    if (!done.length) return null;
    return done.reduce((a, b) => (b.number > a.number ? b : a));
}

function dxRow(p, i) {
    const t = p.transfer || {};
    // Ensamblado de PREÑEZ (admin-pregnancies.md): embryo trae donor/sire PLANOS
    // (lineage ya resuelto server-side), no anidados en `lineage`.
    const e = t.embryo || {};
    const st = PREG_STATE[p.state];
    const last = lastDx(p.diagnostics);
    const lastLabel = last ? `dx${last.number}: ${DX_RESULT_BY_VALUE[last.result]?.short || last.result}${last.date ? ` (${esc(formatDateOnly(last.date))})` : ''}` : '—';
    // próxima fecha estimada: scheduled_for del next_dx, o el forecast del mismo número.
    const fc = p.forecast || {};
    const nextEst = p.next_dx ? (p.next_dx.scheduled_for || fc[`dx${p.next_dx.number}_estimated`] || null) : null;
    const next = p.next_dx ? `dx${p.next_dx.number}: ${date(nextEst)}` : '—';
    // preservación (fresco/congelado): el storage no está en el ensamblado de preñez,
    // lo adjunta el modal desde la sesión TE (_storage).
    const preserv = p._storage ? esc(storageLabel(p._storage)) : '—';
    return `<tr>
        <td>${i + 1}</td>
        <td>${esc(recipientLabel(t.recipient))}</td>
        <td>${esc(e.donor?.name || '—')} × ${esc(e.sire?.name || '—')}</td>
        <td>${preserv}</td>
        <td>${esc(p._farm?.name || t.recipient_farm?.name || '—')}</td>
        <td>${esc(st?.label || p.state || '—')}</td>
        <td>${lastLabel}</td>
        <td>${next}</td>
        <td>${date(p.forecast?.parto_estimated)}</td>
    </tr>`;
}

const NOTE = `<p style="margin-top:20px;font-size:10px;color:#6b7280;font-style:italic;line-height:1.5;">
    Nota: las fechas de dx y parto son <strong>estimaciones</strong> calculadas a partir de la fecha de la
    transferencia; no implican que el evento deba ocurrir exactamente ese día (los procesos biológicos no son
    determinísticos y pueden variar según la receptora y la raza).
</p>`;

// items: array de preñeces ensambladas (GET /admin/pregnancies/:id).
// opts: { opuCode, scopeLabel }.
export function buildDxReport(items, opts = {}) {
    const { opuCode, scopeLabel } = opts;
    const list = (items || []).filter(Boolean);
    const rows = list.map((p, i) => dxRow(p, i)).join('');

    const body = `
        <h1 class="report-title">Informe de diagnósticos${opuCode ? ` · OPU ${esc(opuCode)}` : ''}</h1>
        <p class="report-subtitle">${scopeLabel ? `${esc(scopeLabel)} · ` : ''}${list.length} preñez(es)</p>
        <table>
            <thead><tr>
                <th>#</th><th>Receptora</th><th>Donadora × Toro</th><th>Preserv.</th><th>Finca</th>
                <th>Estado</th><th>Última dx</th><th>Próx. dx (est.)</th><th>Parto (est.)</th>
            </tr></thead>
            <tbody>${rows || '<tr><td colspan="9">Sin preñeces</td></tr>'}</tbody>
        </table>
        ${NOTE}`;

    const slug = (scopeLabel || 'dx').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const filename = `${opuCode ? `${opuCode}-` : ''}informe-dx${slug && slug !== 'dx' ? `-${slug}` : ''}`;

    return { html: reportShell({ bodyHtml: body }), filename, options: reportOptions() };
}

// ─────────────────────────────────────────────────────────────────────────────
// Informe PDF de UNA FASE de dx (dx1 o dx2), por LOTE (finca · sesión TE).
//
// Se alimenta de lo que ya está en memoria en el workspace de Dx: `diagnostics[]`
// del ensamblado de preñez. Es decir SOLO datos GUARDADOS — las marcas que el
// usuario dejó sin guardar viven en el estado `results` del layout y nunca llegan acá.
//
// Columnas: las MISMAS del informe reproductivo (0048) + CL, que se conserva por ser el
// dato clínico propio de la palpación. La agrupación por LOTE (finca · sesión TE) es de
// este informe; el reproductivo agrupa por finca.
//
// A4 APAISADO: con 11 columnas el vertical recorta. Además `table-layout: fixed` +
// anchos en % + `word-wrap` hacen que, si el renderer ignorara la orientación, el
// contenido se envuelva en vez de cortarse.

const slugify = (s) => String(s || '').toLowerCase()
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')   // quita tildes: "La Peña" → "la-pena"
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// CL legible (dónde se palpó) — mismo formato que la grilla en pantalla.
function fmtCL(cl) {
    if (!cl) return null;
    const parts = [];
    if (cl.corpusLuteum?.left) parts.push('Izq');
    if (cl.corpusLuteum?.right) parts.push('Der');
    if (cl.quality) parts.push(`C${cl.quality}`);
    if (cl.cavernous) parts.push('cav');
    return parts.length ? parts.join(' · ') : null;
}

// dx GUARDADO de una fase concreta (con resultado; los sin resultado son placeholders).
const savedDx = (diagnostics, phase) =>
    (diagnostics || []).find((d) => d.number === phase && d.result != null) || null;

// ── Paridad con el informe reproductivo (0048) ────────────────────────────────
// Estado y % de preñez se derivan EXACTAMENTE como en el backend (`services/kpi/transfer_report.js`).
// Es deliberado replicar la fórmula acá: si el front improvisara la suya, los dos informes
// mostrarían cifras distintas para la misma finca.
//
// Ojo con el matiz: NO basta el `state` de la preñez — también pesa el último dx con resultado.
// Una preñez todavía `open` pero con dx1 positivo YA cuenta como preñada.
const PREGNANT_STATES = ['confirmed', 'born'];   // pregnancies.js:17
const PREGNANT_DX = ['positive', 'ppl'];         // 0064/0065: `ppl` confirma

// OJO — el orden de las ramas importa y NO es intuitivo: una preñez `lost` cuyo último dx fue
// positivo cae en la PRIMERA rama y cuenta como preñada. Es el comportamiento del backend
// (transfer_report.js:56-59), no un descuido acá: replicarlo es lo que mantiene ambos informes
// en la misma cifra. En la práctica casi no ocurre — una pérdida suele venir de un dx negativo,
// que sí gana (PregnancyManager._syncDxLoss). No "corregir" sin cambiar antes el backend.
function statusOf(p) {
    const latest = lastDx(p.diagnostics)?.result || null;
    if (PREGNANT_STATES.includes(p.state) || PREGNANT_DX.includes(latest)) return 'pregnant';
    if (p.state === 'lost' || latest === 'negative') return 'empty';
    return 'transferred';   // sin dx aún
}

// Mismo par fondo/texto que el reproductivo (paridad de color) + un borde del mismo tono para
// que la píldora tenga contorno a 9px, donde un relleno plano se ve lavado.
const STATUS = {
    pregnant:    { label: 'Preñada',     bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
    transferred: { label: 'Transferida', bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
    empty:       { label: 'Vacía',       bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
};

function statusBadge(status) {
    const st = STATUS[status]
        || { label: status || '—', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
    return `<span class="badge dxp-badge" style="background:${st.bg};color:${st.color};border-color:${st.border};">${esc(st.label)}</span>`;
}

// MISMA fórmula del backend (transfer_report.js:20-27): pregnant/(pregnant+empty). El denominador
// EXCLUYE las transferidas-sin-dx → es la tasa DIAGNOSTICADA, no "preñadas sobre el total".
// `null` mientras no haya ninguna diagnosticada (no es 0%: es "todavía no se sabe").
const round2 = (n) => Math.round(n * 100) / 100;
const num = (n) => (n == null ? '—' : new Intl.NumberFormat('es-CO').format(Number(n)));
const pct = (v) => (v == null ? '—' : `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(Number(v))}%`);

function summarize(items) {
    const st = items.map(statusOf);
    const pregnant = st.filter((s) => s === 'pregnant').length;
    const emptyN = st.filter((s) => s === 'empty').length;
    const den = pregnant + emptyN;
    return {
        total: items.length,
        pregnant,
        transferred: st.filter((s) => s === 'transferred').length,
        empty: emptyN,
        pregnancy_rate: den > 0 ? round2((pregnant / den) * 100) : null,
    };
}

// Tarjetas del lote — mismas métricas y orden que el informe reproductivo.
function summaryCards(s) {
    const items = [
        ['Total', num(s.total)],
        ['Preñadas', num(s.pregnant)],
        ['% Preñez', pct(s.pregnancy_rate)],
        ['Transferidas', num(s.transferred)],
        ['Vacías', num(s.empty)],
    ];
    return `<div class="dxp-sum">${items.map(([l, v]) =>
        `<div class="dxp-sum__item"><strong>${esc(l)}</strong><span>${v}</span></div>`).join('')}</div>`;
}

// dxN como LETRA (P/V/PPL/?/R) — mismas columnas DX1/DX2 del reproductivo. Solo cuenta el dx
// GUARDADO con resultado; los placeholders sin resultado caen a '—'.
const dxLetter = (diagnostics, n) => {
    const d = savedDx(diagnostics, n);
    return d ? esc(DX_RESULT_BY_VALUE[d.result]?.short || d.result) : '—';
};

// Parto probable: el reproductivo solo lo muestra en las filas Preñadas → misma condición acá,
// vía `statusOf` (no `state` crudo). La fecha es ESTIMADA (`forecast`, 0026/0030); la nota al pie
// lo aclara — el reproductivo usa la `due_date` real, así que puede diferir en días.
const partoCell = (p) => (statusOf(p) === 'pregnant' ? date(p.forecast?.parto_estimated) : '—');

// Fila con las MISMAS columnas del informe reproductivo (0048) + CL, que se conserva por ser
// el dato clínico de la palpación:
//   # · Receptora · Donadora · Toro · Emb. · Fecha · CL · DX1 · DX2 · Parto prob. · Estado
function phaseRow(p, i) {
    const l = p.embryo?.lineage || {};
    const cl = fmtCL(p.cl);
    return `<tr>
        <td class="dxp-num">${i + 1}</td>
        <td class="dxp-left">
            <span class="dxp-recipient">${esc(p.recipient?.number || '—')}</span>
            ${p.recipient?.name ? `<span class="dxp-sub">${esc(p.recipient.name)}</span>` : ''}
        </td>
        <td class="dxp-left">${esc(l.donor?.name || '—')}</td>
        <td class="dxp-left">${esc(l.sire?.name || '—')}</td>
        <td>${esc(p.embryo?.code || '—')}</td>
        <td>${date(p.sessionDates?.te)}</td>
        <td>${cl ? esc(cl) : '—'}</td>
        <td>${dxLetter(p.diagnostics, 1)}</td>
        <td>${dxLetter(p.diagnostics, 2)}</td>
        <td>${partoCell(p)}</td>
        <td>${statusBadge(statusOf(p))}</td>
    </tr>`;
}

// Resumen de resultados guardados del lote: P 8 · V 3 · Pendiente 1
function groupTally(items, phase) {
    const counts = new Map();
    let pending = 0;
    items.forEach((p) => {
        const dx = savedDx(p.diagnostics, phase);
        if (!dx) { pending += 1; return; }
        counts.set(dx.result, (counts.get(dx.result) || 0) + 1);
    });
    const parts = DX_RESULTS.filter((r) => counts.get(r.value))
        .map((r) => `<span class="dxp-tally__item dxp-tally__item--${r.tone}">${esc(r.label)} <b>${counts.get(r.value)}</b></span>`);
    if (pending) parts.push(`<span class="dxp-tally__item dxp-tally__item--none">Pendiente <b>${pending}</b></span>`);
    return parts.join('');
}

function groupBlock(g, phase) {
    const d = g.dates || {};
    const ctx = [
        g.opu ? `<span class="dxp-group__ctx">${esc(g.opu)}</span>` : '',
        g.te ? `<span class="dxp-group__ctx">${esc(g.te)}</span>` : '',
    ].join('');
    const dates = [
        d.te ? `<span><b>TE</b> ${date(d.te)}</span>` : '',
        d.dx1 ? `<span><b>dx1</b> ${date(d.dx1)}</span>` : '',
        d.dx2 ? `<span><b>dx2</b> ${date(d.dx2)}</span>` : '',
    ].join('');

    return `<section class="dxp-group">
        <div class="dxp-group__head">
            <div class="dxp-group__id">
                <span class="dxp-group__farm">${esc(g.farm || 'Sin finca')}</span>
                ${ctx}
                <span class="dxp-group__count">${g.items.length} receptora(s)</span>
            </div>
            ${dates ? `<div class="dxp-group__dates">${dates}</div>` : ''}
        </div>
        ${summaryCards(summarize(g.items))}
        <div class="dxp-tally">${groupTally(g.items, phase)}</div>
        <table class="dxp-table">
            <colgroup>
                <col style="width:3%" /><col style="width:13%" /><col style="width:12%" />
                <col style="width:12%" /><col style="width:8%" /><col style="width:9%" />
                <col style="width:10%" /><col style="width:5%" /><col style="width:5%" />
                <col style="width:10%" /><col style="width:13%" />
            </colgroup>
            <thead><tr>
                <th>#</th><th>Receptora</th><th>Donadora</th><th>Toro</th><th>Emb.</th>
                <th>Fecha</th><th>CL</th><th>DX1</th><th>DX2</th><th>Parto prob.</th><th>Estado</th>
            </tr></thead>
            <tbody>${g.items.map((p, i) => phaseRow(p, i)).join('')}</tbody>
        </table>
    </section>`;
}

const PHASE_CSS = `
  .report-content { padding: 6mm 10mm 6mm; }
  .dxp-meta { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 14px; }
  .dxp-meta__item { background: #f1faf8; border: 1px solid #d6efe9; border-radius: 8px; padding: 5px 12px; }
  .dxp-meta__item strong { display: block; font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
  .dxp-meta__item span { font-weight: 600; color: #111827; font-size: 11px; }

  /* Un lote no se parte si cabe entero; si no cabe, la cabecera nunca queda huérfana. */
  .dxp-group { margin-bottom: 14px; }
  .dxp-group__head { display: flex; align-items: baseline; justify-content: space-between;
      gap: 12px; padding: 6px 8px; background: #ecfdf5; border-left: 3px solid #06796a; border-radius: 3px;
      page-break-after: avoid; break-after: avoid; }
  .dxp-group__id { display: flex; align-items: baseline; gap: 8px; }
  .dxp-group__farm { font-size: 13px; font-weight: 700; color: #0f172a; }
  .dxp-group__ctx { font-size: 10px; color: #6b7280; }
  .dxp-group__count { font-size: 10px; color: #6b7280; }
  .dxp-group__dates { display: flex; gap: 10px; font-size: 10px; color: #6b7280; white-space: nowrap; }
  .dxp-group__dates b { color: #0f172a; }

  /* Mismo lenguaje visual que las píldoras de Estado (9px + borde del mismo tono), para que
     las dos bandas de chips del lote no compitan entre sí. */
  .dxp-tally { display: flex; flex-wrap: wrap; gap: 5px; margin: 5px 0; page-break-after: avoid; break-after: avoid; }
  .dxp-tally__item { display: inline-block; white-space: nowrap;
      font-size: 9px; font-weight: 600; padding: 1px 7px; border-radius: 999px;
      border: 1px solid #e2e8f0; background: #f8fafc; color: #475569; }
  .dxp-tally__item b { color: #0f172a; font-weight: 700; }
  .dxp-tally__item--good { background: #dcfce7; border-color: #bbf7d0; color: #166534; }
  .dxp-tally__item--bad { background: #fee2e2; border-color: #fecaca; color: #991b1b; }
  .dxp-tally__item--warn { background: #fef3c7; border-color: #fde68a; color: #92400e; }
  .dxp-tally__item--none { background: #f8fafc; border-color: #e2e8f0; color: #64748b; }

  /* Tarjetas de resumen del lote (Total/Preñadas/% Preñez/Transferidas/Vacías) — mismas
     métricas que el informe reproductivo. Nunca se separan de su tabla. */
  .dxp-sum { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0; page-break-after: avoid; break-after: avoid; }
  .dxp-sum__item { min-width: 62px; padding: 3px 10px; text-align: center;
      background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; }
  .dxp-sum__item strong { display: block; font-size: 8.5px; font-weight: 600; color: #6b7280;
      text-transform: uppercase; letter-spacing: 0.03em; }
  .dxp-sum__item span { font-size: 12px; font-weight: 700; color: #0f172a; }

  /* fixed + % + break-word: si el renderer ignorara el apaisado, envuelve en vez de recortar. */
  .dxp-table { table-layout: fixed; width: 100%; }
  .dxp-table td, .dxp-table th { word-wrap: break-word; overflow-wrap: break-word; }
  .dxp-table thead th { font-size: 10px; padding: 6px 5px; }
  .dxp-table tbody td { font-size: 10.5px; padding: 5px; }
  /* Estado. El .badge del shell (11px, inline, padding 2px 10px) está calibrado para los
     informes verticales: acá queda MÁS GRANDE que el texto de la celda (10.5px) y, en una
     columna del 13% en apaisado, "Transferida" parte la píldora en dos líneas por el
     word-wrap de la tabla. Se re-estila SOLO en este informe — shell.js es compartido
     con el reproductivo y cambiarlo movería también ese PDF.
       · inline-block → el padding vertical genera caja (inline lo ignora)
       · nowrap       → la píldora nunca se parte
       · 9px/700      → más chica que el texto pero legible por el peso */
  .dxp-table .dxp-badge {
      display: inline-block; white-space: nowrap;
      padding: 1px 7px; border-radius: 999px; border: 1px solid transparent;
      font-size: 9px; font-weight: 700; line-height: 1.6; letter-spacing: 0.01em;
  }

  .dxp-num { color: #94a3b8; }
  .dxp-left { text-align: left; }
  .dxp-recipient { font-weight: 700; color: #0f172a; }
  .dxp-sub { display: block; font-size: 9px; color: #6b7280; }
`;

// groups: [{ key, farm, opu, te, dates, items }] — los lotes SELECCIONADOS del workspace.
// opts: { phase: 1|2, opuCode, scopeLabel, journeyDate }.
export function buildDxPhaseReport(groups, opts = {}) {
    const { phase = 1, opuCode, scopeLabel, journeyDate } = opts;
    const list = (groups || []).filter((g) => g && g.items?.length);
    const total = list.reduce((n, g) => n + g.items.length, 0);
    const withResult = list.reduce(
        (n, g) => n + g.items.filter((p) => savedDx(p.diagnostics, phase)).length, 0);

    const meta = [
        opuCode ? `<div class="dxp-meta__item"><strong>OPU</strong><span>${esc(opuCode)}</span></div>` : '',
        `<div class="dxp-meta__item"><strong>Fase</strong><span>dx${phase}</span></div>`,
        `<div class="dxp-meta__item"><strong>Receptoras</strong><span>${total}</span></div>`,
        `<div class="dxp-meta__item"><strong>Dx registrados</strong><span>${withResult} de ${total}</span></div>`,
        journeyDate ? `<div class="dxp-meta__item"><strong>Jornada</strong><span>${date(journeyDate)}</span></div>` : '',
    ].join('');

    const body = `
        <h1 class="report-title">Informe de diagnósticos · dx${phase}${opuCode ? ` · OPU ${esc(opuCode)}` : ''}</h1>
        <p class="report-subtitle">${scopeLabel ? `${esc(scopeLabel)} · ` : ''}${list.length} lote(s) · ${total} receptora(s)</p>
        <div class="dxp-meta">${meta}</div>
        ${list.length ? summaryCards(summarize(list.flatMap((g) => g.items))) : ''}
        ${list.length ? list.map((g) => groupBlock(g, phase)).join('') : '<p>Sin lotes seleccionados.</p>'}
        <p style="margin-top:16px;font-size:9.5px;color:#6b7280;font-style:italic;line-height:1.5;">
            Cada <strong>lote</strong> agrupa las receptoras de una misma finca y sesión de transferencia (TE);
            una finca con dos sesiones TE aparece como dos lotes.<br/>
            Este informe refleja únicamente los diagnósticos <strong>ya guardados</strong>. Las marcas
            ingresadas en pantalla que aún no se hayan guardado no aparecen acá.
        </p>`;

    const slug = slugify(scopeLabel);
    const filename = `${opuCode ? `${opuCode}-` : ''}informe-dx${phase}${slug ? `-${slug}` : ''}`;

    return {
        html: reportShell({ bodyHtml: body, landscape: true, extraCss: PHASE_CSS }),
        filename,
        options: reportOptions({ landscape: true }),
    };
}
